import { computed } from "vue";

import { useReleaseStore } from "@/stores/release-store";

/**
 * Top offset for a screen-edge right-side panel (ThreadDrawer,
 * DomainObjectDrawer).
 *
 * Those panels start below the menubar, but two things can sit between the
 * menubar and the panel and would otherwise paint over its header:
 *   - the release banner, shown while viewing a release (but not on a thread
 *     branch, which shows the accent strip instead);
 *   - the 3px `.thread-accent-strip`, a pink line pinned under the menubar at a
 *     z-index above the panel, shown while viewing a thread branch.
 *
 * Shared so a new panel cannot be written without the offsets — the original
 * copy of this logic lived only in ThreadDrawer, and the second panel was
 * written without it.
 */
export const useRightPanelOffset = () => {
    const releaseStore = useReleaseStore();

    const bannerVisible = computed(
        () =>
            Boolean(releaseStore.activeRelease) &&
            !releaseStore.viewingThreadBranch
    );

    const accentStripVisible = computed(() => releaseStore.viewingThreadBranch);

    /** Ready-to-bind `padding-top` value for the panel's root element. */
    const panelOffset = computed(() => {
        if (bannerVisible.value) {
            return "calc(var(--menu-bar-height) + var(--release-banner-height))";
        }
        if (accentStripVisible.value) {
            return "calc(var(--menu-bar-height) + var(--thread-accent-strip-height))";
        }
        return "var(--menu-bar-height)";
    });

    return { bannerVisible, accentStripVisible, panelOffset };
};
